import React from 'react';
import PropTypes from 'prop-types';
import { LuClipboardList } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { EmptyState } from '../../ProfilePage/components/ProfileUi';
import useSmoText from '../smoText';

/** Every unfinished visit and calendar task of the team in one list. */
const IncompleteTasksDialog = ({ isOpen, onClose, incompleteTasks }) => {
  const { t, label, language, locale } = useSmoText();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={label('tasks.dialogTitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30" size="xs">
          <LuClipboardList aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}
    >
      {incompleteTasks.total === 0 ? (
        <EmptyState icon={LuClipboardList} title={label('tasks.allDone')} />
      ) : (
        <div className="tw-space-y-3">
          <p className="tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">
            {label('tasks.summary', { total: incompleteTasks.total, visits: incompleteTasks.visits.length, jobs: incompleteTasks.jobs.length })}
          </p>
          <div className="tw-relative tw-overflow-x-auto tw-rounded-2xl tw-border tw-border-slate-200/80 glass-scrollbar dark:tw-border-white/10">
            <table className="tw-w-full tw-border-collapse tw-text-sm">
              <thead className="tw-bg-slate-50/90 dark:tw-bg-white/[0.03]">
                <tr>
                  {['date', 'worker', 'type', 'description', 'status'].map((key) => (
                    <th key={key} scope="col" className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400">{label(`tasks.columns.${key}`)}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="tw-divide-y tw-divide-slate-200/70 tw-text-slate-700 dark:tw-divide-white/[0.06] dark:tw-text-slate-200">
                {incompleteTasks.all.map((task, index) => (
                  <tr key={task.id || index}>
                    <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5 tw-tabular-nums">{task.date ? new Date(task.date).toLocaleDateString(locale) : '-'}</td>
                    <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5">{task.workerName}</td>
                    <td className="tw-px-4 tw-py-2.5">
                      <span className={`tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold ${task.type === 'visit' ? 'tw-bg-sky-500/15 tw-text-sky-800 dark:tw-text-sky-200' : 'tw-bg-violet-500/15 tw-text-violet-800 dark:tw-text-violet-200'}`}>
                        {task.type === 'visit' ? label('tasks.visit') : label('tasks.calendarJob')}
                      </span>
                    </td>
                    <td className="tw-whitespace-normal tw-px-4 tw-py-2.5">
                      {task.type === 'visit' ? (
                        <>
                          <strong className="tw-block">{task.patientId || label('tasks.patient')}</strong>
                          <small className="tw-text-slate-500 dark:tw-text-slate-400">{task.visitType === 'home' ? label('tasks.homeVisit') : label('tasks.siteVisit')}</small>
                        </>
                      ) : (
                        <>
                          <strong className="tw-block">{task.title}</strong>
                          {task.description && <small className="tw-text-slate-500 dark:tw-text-slate-400">{task.description}</small>}
                        </>
                      )}
                    </td>
                    <td className="tw-px-4 tw-py-2.5">
                      <span className="tw-rounded-full tw-bg-orange-500/15 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-orange-800 dark:tw-text-orange-200">{label('tasks.notDone')}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
};

IncompleteTasksDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  incompleteTasks: PropTypes.object.isRequired,
};

export default IncompleteTasksDialog;
